package org.vreo.devicehub.features.device.presentation.deviceList

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.onStart
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope

import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.domain.device.device.AnprDevice
import org.vreo.devicehub.features.core.domain.onError
import org.vreo.devicehub.features.core.domain.onSuccess
import org.vreo.devicehub.features.core.presentation.fileSaver.FileSaver
import org.vreo.devicehub.features.core.presentation.toUiText
import org.vreo.devicehub.features.device.domain.DeviceAccessRepository

/**
 * Represents the state of the Device List screen.
 *
 * @param authenticationToken user authentication token
 * @param repository device access repository for accessing remote device date
 */
class DeviceListViewModel(
    private val authenticationToken: String,
    private val repository: DeviceAccessRepository,
    isAdmin: Boolean
) : ViewModel() {
    private val _state = MutableStateFlow(DeviceListState())
    val state = _state
        .onStart {
            _state.update { it.copy(isAdmin = isAdmin) }
            updateDeviceList()
        }
        .stateIn(
            scope = viewModelScope,
            started = SharingStarted.WhileSubscribed(5000L),
            initialValue = _state.value
        )

    /**
     * Handel all user actions
     *
     * @param action action to handle
     */
    fun onAction(action: DeviceListActions) {
        when (action) {
            is DeviceListActions.OnClearError -> {
                _state.update {
                    it.copy(
                        error = null
                    )
                }
            }

            is DeviceListActions.OnUpdateDeviceList -> {
                updateDeviceList()
            }

            is DeviceListActions.OnDeviceSelect -> {}

            is DeviceListActions.OnAddNewDevice -> {}

            is DeviceListActions.OnRegisterNewDevice -> {}

            is DeviceListActions.OnSaveProductionDeviceDetails -> {
                getAndStoreProductionDeviceDetails()
            }

            is DeviceListActions.OnClearConfirmDialog -> {
                _state.update {
                    it.copy(
                        confirmAlertItem = null,
                        selectedDevice = null
                    )
                }
            }

            is DeviceListActions.OnOpenUnlinkDeviceDialog -> {
                _state.update {
                    it.copy(
                        confirmAlertItem = DeviceMenuAlertItem.UNLINK,
                        selectedDevice = action.device
                    )
                }
            }

            is DeviceListActions.OnOpenDeleteDeviceDialog -> {
                _state.update {
                    it.copy(
                        confirmAlertItem = DeviceMenuAlertItem.DELETE,
                        selectedDevice = action.device
                    )
                }
            }

            is DeviceListActions.OnUnlinkConfirm -> {
                unlinkDevice()
            }

            is DeviceListActions.OnDeleteConfirm -> {
                deleteDevice()
            }

            is DeviceListActions.OnAdminViewToggleUpdate -> {
                _state.update {
                    it.copy(
                        adminViewEnable = action.value
                    )
                }
            }
        }
    }

    /**
     * Updates the device list.
     */
    private fun updateDeviceList() = viewModelScope.launch {
        runCatching {
            _state.update {
                it.copy(
                    isLoading = true
                )
            }

            repository.getUserDevices(authenticationToken)
                .onSuccess { data ->
                    val sortedDevices = data.sortedWith(
                        compareByDescending<AnprDevice> { it.active }
                            .thenBy { it.serialNumber.toIntOrNull() ?: 0 }
                    )

                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = null,
                            devices = sortedDevices
                        )
                    }
                }
                .onError { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = error.toUiText()
                        )
                    }
                }

        }.getOrElse {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Remote.UNKNOWN.toUiText()
                )
            }
        }
    }

    /**
     * Unlink a device from user's account
     */
    private fun unlinkDevice() = viewModelScope.launch {
        _state.update {
            it.copy(
                isLoading = true
            )
        }

        if (_state.value.selectedDevice == null) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.INVALID_ARGUMENT.toUiText()
                )
            }
            return@launch
        }

        runCatching {
            repository.unlinkDeviceFromAccount(
                token = authenticationToken,
                deviceId = _state.value.selectedDevice?.deviceId ?: ""
            )
                .onSuccess {
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = null
                        )
                    }
                    onAction(DeviceListActions.OnUpdateDeviceList())
                }
                .onError { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = error.toUiText()
                        )
                    }
                }

        }.getOrElse {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Remote.UNKNOWN.toUiText()
                )
            }
        }
    }

    /**
     * Delete a device from production environment
     */
    private fun deleteDevice() = viewModelScope.launch {
        _state.update {
            it.copy(
                isLoading = true
            )
        }

        if (_state.value.selectedDevice == null) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.INVALID_ARGUMENT.toUiText()
                )
            }
            return@launch
        }

        runCatching {
            repository.deleteDeviceFromProduction(
                token = authenticationToken,
                deviceId = _state.value.selectedDevice?.deviceId ?: ""
            )
                .onSuccess {
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = null
                        )
                    }
                    onAction(DeviceListActions.OnUpdateDeviceList())
                }
                .onError { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = error.toUiText()
                        )
                    }
                }

        }.getOrElse {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Remote.UNKNOWN.toUiText()
                )
            }
        }
    }

    private fun getAndStoreProductionDeviceDetails() = viewModelScope.launch {
        _state.update {
            it.copy(
                isLoading = true
            )
        }

        runCatching {
            repository.getDeviceProductionRegister(authenticationToken)
                .onSuccess { data ->
                    FileSaver.save(
                        fileName = "production_devices.xlsx",
                        bytes = data,
                        mimeType = "application/xlsx"
                    )

                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = null
                        )
                    }
                }
                .onError { error ->
                    _state.update(
                        {
                            it.copy(
                                isLoading = false,
                                error = error.toUiText()
                            )
                        }
                    )
                }
        }.getOrElse {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Remote.UNKNOWN.toUiText()
                )
            }
        }
    }
}